import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store, { getLayerOpacity } from '../state';
import ManifestTimelineHelper from '../services/ManifestTimelineHelper';
import { authenticatedFetch, getApiServer } from '../config';

export class LightningLayer extends BaseProduct {
  public readonly id: ProductId = 'lightning';
  private sourceId = 'lightning-geojson-source';
  private unsubscribeStore: (() => void) | null = null;

  protected onActivate(): void {
    this.refresh();
    let lastModel = store.getState().selectedModels[this.id] || 'blend';
    let lastViewMode = store.getState().lightningViewMode || 'daily';
    this.unsubscribeStore = store.subscribe((s) => {
      if (!this.active) return;
      const currentModel = s.selectedModels[this.id] || 'blend';
      const currentViewMode = s.lightningViewMode || 'daily';
      if (currentModel !== lastModel || currentViewMode !== lastViewMode) {
        lastModel = currentModel;
        lastViewMode = currentViewMode;
        this.refresh();
      }
    });
  }

  protected onDeactivate(): void {
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
    if (this.map.getLayer(this.id)) this.map.removeLayer(this.id);
    if (this.map.getSource(this.sourceId)) this.map.removeSource(this.sourceId);
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public async refresh() {
    if (!this.active) return;
    const state = store.getState();
    const current = state.currentTimeStep;
    if (!current) return;

    const manifest = state.manifest;
    const viewMode = state.lightningViewMode || 'daily';
    const model = state.selectedModels[this.id] || 'blend';

    let jsonFileName = '';
    let isBlend = (model === 'blend' || !model);
    let dateStr = current.toISOString().split('T')[0];

    if (viewMode === 'daily') {
      const clampedFrame = manifest ? (
        ManifestTimelineHelper.getClampedFrame(manifest, 'lightning_24h' as ProductId, model, current) ||
        ManifestTimelineHelper.getClampedFrame(manifest, 'lightning_24h' as ProductId, 'blend', current) ||
        ManifestTimelineHelper.getClampedFrame(manifest, 'lightning', model, current) ||
        ManifestTimelineHelper.getClampedFrame(manifest, 'lightning', 'blend', current)
      ) : null;
      dateStr = clampedFrame ? clampedFrame.dateStr : current.toISOString().split('T')[0];
      jsonFileName = isBlend ? `lightning_24h_${dateStr}.json` : `lightning_24h_${model}_${dateStr}.json`;
    } else {
      const clampedFrame = manifest ? ManifestTimelineHelper.getClampedFrame(manifest, 'lightning', model, current) : null;
      dateStr = clampedFrame ? clampedFrame.dateStr : current.toISOString().split('T')[0];
      const hourStr = clampedFrame ? clampedFrame.hourStr.replace(/^Z/, '') : String(current.getUTCHours()).padStart(2, '0');
      jsonFileName = isBlend
        ? `lightning_${dateStr}_Z${hourStr}.json`
        : `lightning_${model}_${dateStr}_Z${hourStr}.json`;
    }

    const apiServer = getApiServer(manifest);
    
    const tileRoot = (manifest ? manifest.tile_base : "tiles").replace(/^\/+|\/+$/g, '');
    const cleanServer = apiServer.replace(/\/$/, '');
    let cleanPath = `${tileRoot}/forecast/lightning/lightning_forecast/${jsonFileName}`.replace(/\/+/g, '/');
    if (cleanPath.startsWith('/')) {
      cleanPath = cleanPath.substring(1);
    }
    const url = `${cleanServer}/${cleanPath}`;

    try {
      let res = await authenticatedFetch(url);
      if (!res.ok && viewMode === 'daily' && !isBlend) {
        // Fallback to blend daily file if individual model daily file is not found
        const fallbackPath = `${tileRoot}/forecast/lightning/lightning_forecast/lightning_24h_${dateStr}.json`.replace(/\/+/g, '/');
        const fallbackUrl = `${cleanServer}/${fallbackPath.startsWith('/') ? fallbackPath.substring(1) : fallbackPath}`;
        res = await authenticatedFetch(fallbackUrl);
      }
      if (!res.ok) {
        this.clearLightningData();
        return;
      }
      
      const geojson = await res.json();
      
      // Update existing GeoJSON source
      const source = this.map.getSource(this.sourceId) as maplibregl.GeoJSONSource;
      if (source) {
        source.setData(geojson);
        if (this.map.getLayer(this.id)) {
          this.map.setPaintProperty(this.id, 'fill-opacity', 0.6 * getLayerOpacity(this.id, 1.0));
        }
      } else {
        // Add source and layer
        this.map.addSource(this.sourceId, {
          type: 'geojson',
          data: geojson
        });

        this.map.addLayer({
          id: this.id,
          type: 'fill',
          source: this.sourceId,
          paint: {
            'fill-color': [
              'match',
              ['get', 'level'],
              1, '#9664ff',
              2, '#ff8000',
              3, '#ff0000',
              'transparent'
            ],
            'fill-opacity': 0.6 * getLayerOpacity(this.id, 1.0)
          }
        });
      }
    } catch (e) {
      console.warn('Failed to load lightning hazards:', e);
      this.clearLightningData();
    }
  }

  private clearLightningData() {
    const source = this.map.getSource(this.sourceId) as maplibregl.GeoJSONSource;
    if (source) {
      source.setData({
        type: 'FeatureCollection',
        features: []
      });
    }
  }
}
export default LightningLayer;
